import React from 'react'
import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import Footer from '../components/Footer'

const DataProcessingAddendum = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="container-padding py-12 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Data Processing Addendum (DPA)</h1>
          <p className="text-sm text-gray-600 mb-2">FlowBeatIQ</p>
          <p className="text-sm text-gray-600 mb-8">Last Updated: December 28, 2025</p>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-8">
            <p className="text-gray-700">
              This Data Processing Addendum ("DPA") forms part of the <Link to="/terms" className="text-blue-600 hover:text-blue-700">Terms of Service</Link> between
              Rigel Labs LLC ("Processor" or "FlowBeatIQ") and the customer ("Controller") and applies to the processing of visitor analytics data.
            </p>
          </div>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Definitions</h2>
              <p className="text-gray-700 mb-4">
                <strong>"Applicable Data Protection Laws"</strong> means all privacy and data protection laws applicable to the
                processing of personal data, including the GDPR, UK GDPR, and CCPA where applicable.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural person,
                as defined under Applicable Data Protection Laws.
              </p>
              <p className="text-gray-700">
                <strong>"Processing"</strong> has the meaning given under Applicable Data Protection Laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Roles of the Parties</h2>
              <p className="text-gray-700 mb-4">
                Customer is the <strong>Data Controller</strong> with respect to visitor analytics data.
              </p>
              <p className="text-gray-700">
                FlowBeatIQ acts solely as a <strong>Data Processor</strong>, processing data on behalf of and under the
                instructions of the Customer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Scope of Processing</h2>
              <p className="text-gray-700 mb-4">
                FlowBeatIQ processes visitor analytics data exclusively for the purpose of providing foot traffic analytics
                and related insights.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Processing activities include:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Collection of WiFi probe signals</li>
                <li>One-way hashing of device identifiers with rotating salts</li>
                <li>Aggregation and analysis of anonymized visitor counts, dwell time, and trends</li>
                <li>Storage and display of analytics in dashboards</li>
              </ul>
              <p className="text-gray-700">
                FlowBeatIQ does not process personal data for its own purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Types and Categories</h2>
              <p className="text-gray-700 mb-4">
                <strong>Categories of data subjects:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Visitors to Customer's business locations</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Types of data:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Hashed device identifiers</li>
                <li>Signal strength (RSSI)</li>
                <li>Timestamps</li>
                <li>Sensor identifiers and location metadata</li>
              </ul>
              <p className="text-gray-700">
                <strong className="text-blue-600">No names, contact details, or directly identifiable personal data are processed.</strong>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Processing Instructions</h2>
              <p className="text-gray-700 mb-4">
                FlowBeatIQ shall process visitor analytics data only:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>In accordance with this DPA</li>
                <li>In accordance with the Terms of Service and Privacy Policy</li>
                <li>On documented instructions from the Customer</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Security Measures</h2>
              <p className="text-gray-700 mb-4">
                FlowBeatIQ implements appropriate technical and organizational security measures, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Encryption of data in transit and at rest</li>
                <li>Access controls and least-privilege policies</li>
                <li>Continuous monitoring and logging</li>
                <li>Regular security reviews</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Subprocessors</h2>
              <p className="text-gray-700 mb-4">
                Customer authorizes FlowBeatIQ to engage subprocessors for infrastructure, analytics, email delivery,
                and payment processing.
              </p>
              <p className="text-gray-700">
                FlowBeatIQ ensures subprocessors are bound by data protection obligations consistent with this DPA.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Data Transfers</h2>
              <p className="text-gray-700">
                Where personal data is transferred outside the Customer's jurisdiction, FlowBeatIQ relies on legally
                recognized transfer mechanisms, including Standard Contractual Clauses (SCCs), where required.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Data Subject Rights</h2>
              <p className="text-gray-700">
                FlowBeatIQ shall assist Customer, where reasonably possible, in responding to data subject requests
                under Applicable Data Protection Laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Data Breach Notification</h2>
              <p className="text-gray-700">
                FlowBeatIQ will notify Customer without undue delay upon becoming aware of a personal data breach
                affecting visitor analytics data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Data Retention and Deletion</h2>
              <p className="text-gray-700">
                Visitor analytics data is retained in accordance with the Customer's subscription plan and deleted or
                anonymized upon termination of the Service, unless retention is required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Liability</h2>
              <p className="text-gray-700">
                This DPA does not modify or expand liability beyond the limitations set forth in the <Link to="/terms" className="text-blue-600 hover:text-blue-700">Terms of Service</Link>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
              <p className="text-gray-700">
                This DPA is governed by the same law and dispute resolution provisions as the <Link to="/terms" className="text-blue-600 hover:text-blue-700">Terms of Service</Link>.
              </p>
            </section>

            <div className="border-t border-gray-200 pt-8 mt-8">
              <div className="bg-green-50 border-l-4 border-green-600 p-4 mb-6">
                <p className="text-gray-700 font-semibold">
                  By using the FlowBeatIQ Service, Customer agrees to this Data Processing Addendum.
                </p>
              </div>
              <div className="flex gap-4">
                <Link
                  to="/terms"
                  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors duration-200"
                >
                  View Terms of Service
                </Link>
                <Link
                  to="/privacy"
                  className="inline-block bg-gray-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-gray-700 transition-colors duration-200"
                >
                  View Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default DataProcessingAddendum
